import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Edit3, AlertCircle, CheckCircle } from 'lucide-react';
import { PostStatusDropdown, type PostStatus } from '../common/PostStatusDropdown';
import { BrandDropdown } from '../common/BrandDropdown';
import { SocialIcon } from '../icons/SocialIcon';
import type { Brand } from '../../types';
import type { SocialPlatform } from '../icons/SocialIcon';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updateData: BulkUpdateData) => void;
  selectedPostsCount: number;
  brands: Brand[];
  isLoading?: boolean;
}

export interface BulkUpdateData {
  status?: PostStatus;
  brandId?: string;
  platforms?: SocialPlatform[];
}

const SOCIAL_PLATFORMS: SocialPlatform[] = [
  'facebook',
  'instagram', 
  'linkedin',
  'twitter',
  'tiktok',
  'youtube',
  'pinterest',
  'google-business'
];

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedPostsCount,
  brands,
  isLoading = false
}) => {
  const { t } = useTranslation();
  
  const [updateData, setUpdateData] = useState<BulkUpdateData>({});
  const [enabledFields, setEnabledFields] = useState({
    status: false,
    brand: false,
    platforms: false
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setUpdateData({});
      setEnabledFields({
        status: false,
        brand: false,
        platforms: false
      });
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 BulkEditModal: Form submitted - THIS IS THE ONLY TIME UPDATE SHOULD HAPPEN');
    
    const finalUpdateData: BulkUpdateData = {};
    
    if (enabledFields.status && updateData.status) {
      finalUpdateData.status = updateData.status;
    }
    
    if (enabledFields.brand && updateData.brandId) {
      finalUpdateData.brandId = updateData.brandId;
    }
    
    if (enabledFields.platforms && updateData.platforms && updateData.platforms.length > 0) {
      finalUpdateData.platforms = updateData.platforms;
    }

    // Ensure at least one field is being updated
    const hasUpdates = Object.keys(finalUpdateData).length > 0;
    if (!hasUpdates) {
      alert(t('bulkEdit.noFieldsSelected', 'Please select at least one field to update.'));
      return;
    }

    console.log('📤 BulkEditModal: Calling onConfirm with data:', finalUpdateData);
    onConfirm(finalUpdateData);
  };

  const handlePlatformToggle = (platform: SocialPlatform) => {
    const currentPlatforms = updateData.platforms || [];
    const newPlatforms = currentPlatforms.includes(platform)
      ? currentPlatforms.filter(p => p !== platform)
      : [...currentPlatforms, platform];
    
    setUpdateData(prev => ({ ...prev, platforms: newPlatforms }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-[61]">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Edit3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('bulkEdit.title', 'Bulk Edit Posts')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('bulkEdit.subtitle', 'Update {{count}} selected posts', { count: selectedPostsCount })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">
                  {t('bulkEdit.infoTitle', 'Bulk Update Information')}
                </p>
                <p>
                  {t('bulkEdit.infoMessage', 'Select which fields you want to update and their new values. Only enabled fields will be modified for the selected posts.')}
                </p>
              </div>
            </div>

            {/* Status Update */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="enable-status"
                  checked={enabledFields.status}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setEnabledFields(prev => ({ ...prev, status: isChecked }));
                    // Set default status value when enabling the field
                    if (isChecked && !updateData.status) {
                      setUpdateData(prev => ({ ...prev, status: 'draft' }));
                    }
                  }}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="enable-status" className="text-sm font-medium text-gray-700">
                  {t('bulkEdit.updateStatus', 'Update Status')}
                </label>
              </div>
              
              {enabledFields.status && (
                <div className="ml-7">
                  <PostStatusDropdown
                    value={updateData.status || 'draft'}
                    onChange={(status) => {
                      console.log('📝 MODAL STATUS DROPDOWN: Status changed to', status);
                      setUpdateData(prev => ({ ...prev, status }));
                    }}
                  />
                </div>
              )}
            </div>

            {/* Brand Update */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="enable-brand"
                  checked={enabledFields.brand}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setEnabledFields(prev => ({ ...prev, brand: isChecked }));
                    // Note: Brand selection requires user interaction, no default value set
                  }}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="enable-brand" className="text-sm font-medium text-gray-700">
                  {t('bulkEdit.updateBrand', 'Update Brand')}
                </label>
              </div>
              
              {enabledFields.brand && (
                <div className="ml-7">
                  <BrandDropdown
                    brands={brands.filter(b => b.id !== 'all')}
                    selectedBrandId={updateData.brandId || ''}
                    onBrandSelect={(brandId) => setUpdateData(prev => ({ ...prev, brandId }))}
                  />
                </div>
              )}
            </div>

            {/* Platforms Update */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="enable-platforms"
                  checked={enabledFields.platforms}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setEnabledFields(prev => ({ ...prev, platforms: isChecked }));
                    // Initialize with empty array when enabling platforms
                    if (isChecked && !updateData.platforms) {
                      setUpdateData(prev => ({ ...prev, platforms: [] }));
                    }
                  }}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="enable-platforms" className="text-sm font-medium text-gray-700">
                  {t('bulkEdit.updatePlatforms', 'Update Platforms')}
                </label>
              </div>
              
              {enabledFields.platforms && (
                <div className="ml-7">
                  <p className="text-sm text-gray-600 mb-3">
                    {t('bulkEdit.selectPlatforms', 'Select platforms for publishing:')}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {SOCIAL_PLATFORMS.map(platform => (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => handlePlatformToggle(platform)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          updateData.platforms?.includes(platform)
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <SocialIcon platform={platform} size={20} />
                        <span className="text-sm font-medium capitalize">
                          {platform.replace('_', ' ')}
                        </span>
                      </button>
                    ))}
                  </div>
                  {updateData.platforms?.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      {t('bulkEdit.selectAtLeastOnePlatform', 'Select at least one platform')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isLoading}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            
            <button
              type="submit"
              disabled={isLoading || (!enabledFields.status && !enabledFields.brand && !enabledFields.platforms)}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {t('bulkEdit.updating', 'Updating...')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {t('bulkEdit.updatePosts', 'Update {{count}} Posts', { count: selectedPostsCount })}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 